// src/app/Tracking/vehicle-route/EditVehicleRoute.jsx
import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Polyline,
} from "@react-google-maps/api";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  IconButton,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Chip,
  Tabs,
  Tab,
  Stack,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Map as MapIcon,
  Search as SearchIcon,
  MyLocation as MyLocationIcon,
  Clear as ClearIcon,
  LocationOn,
  CalendarToday as CalendarIcon,
  Info as InfoIcon,
  Undo as UndoIcon,
  ExpandMore as ExpandMoreIcon,
  EditOutlined as EditOutlinedIcon,
  AltRoute as RouteIcon,
  MapOutlined as MapOutlinedIcon,
} from "@mui/icons-material";

import VehicleRouteServices from "../../services/externalServices/trackingservices/vehicleRouteServices";
import OwnerServices from "../../services/externalServices/trackingservices/ownerServices";
import googlePin from "../../../assets/images/icons/googlePin.png";
import { getOwnerDetailsFromLocalStorage } from "../../utils/auth";
import VehicleServices from "../../services/externalServices/trackingservices/vehicleServices";

/* ────────────────────────────────────────────────────────── */
/* Map defaults                                               */
/* ────────────────────────────────────────────────────────── */
const mapContainerStyle = {
  width: "100%",
  height: "500px",
  position: "relative",
};
const hospetCenter = { lat: 15.2697, lng: 76.3869 };
const libraries = ["places", "geometry"];

/* ────────────────────────────────────────────────────────── */
/* Component                                                  */
/* ────────────────────────────────────────────────────────── */
const EditVehicleRoute = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  /* Data state */
  const [routeName, setRouteName] = useState("");
  const [stops, setStops] = useState([]);
  const [segments, setSegments] = useState([]);
  const [assignedOwner, setAssignedOwner] = useState("");
  const [assignedVehicle, setAssignedVehicle] = useState([]);
  const [vehicleList, setVehicleList] = useState([]);
  const [approvalNeeded, setApprovalNeeded] = useState(true);
  const [routeType, setRouteType] = useState("pickup");
  const [returnRouteId, setReturnRouteId] = useState("");
  const [ownerList, setOwnerList] = useState([]);
  const [aliasNumber, setAliasNumber] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  /* New fields */
  const [approvalStatus, setApprovalStatus] = useState("Pending");
  const [isTrainingMode, setIsTrainingMode] = useState(false);
  const [createdAt, setCreatedAt] = useState(null);
  const [newRouteSegments, setNewRouteSegments] = useState([]);

  /* Map state */
  const [map, setMap] = useState(null);
  const [mapCenter, setMapCenter] = useState(hospetCenter);
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(null);
  const [currentStopIndex, setCurrentStopIndex] = useState(null);
  const [showMapDialog, setShowMapDialog] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [currentCoordinates, setCurrentCoordinates] = useState("");


  /* UI state */
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [segmentToDelete, setSegmentToDelete] = useState(null);
  const [tab, setTab] = useState(0); // 0=Details, 1=Stops, 2=Map
  const [editMode, setEditMode] = useState("segments"); // segments | stops
  const isSmall = useMediaQuery("(max-width:900px)");
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

 

 


  /* API key */
  const GOOGLE_MAP_API_KEY = "AIzaSyB4ErDCIgl8ahjk_tmEQ_H3oViYGy80trU";

  /* Refs */
  const searchInputRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const polylineRef = useRef(null);
  const segmentsRef = useRef(segments);

  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);

  /* Google loader */
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
    libraries,
  });

  /* ───────── Data Loaders ───────── */
  const getRouteData = useCallback(async () => {
    if (!id) return;
    try {
      const routeData = await VehicleRouteServices.getVehicleRouteById(id);
      setRouteName(routeData.route_name || "");
      setAliasNumber(routeData.alias_number || "");
      setStartTime(routeData.startTime || "");
      setEndTime(routeData.endTime || "");
      setStops(routeData.stops || []);
      setSegments(routeData.segments || []);
      setAssignedOwner(routeData.assigned_owner_id || "");
      setAssignedVehicle(routeData.assigned_vehicle_id || "");
      setApprovalNeeded(
        routeData.approvalNeeded !== undefined ? routeData.approvalNeeded : true
      );
      setRouteType(routeData.routeType || "pickup");
      setReturnRouteId(routeData.returnRouteId || "");
      setApprovalStatus(routeData.approval_status || "Pending");
      setIsTrainingMode(
        routeData.isTrainingMode !== undefined
          ? routeData.isTrainingMode
          : false
      );
      setCreatedAt(
        routeData.created_at ? new Date(routeData.created_at) : null
      );
      setNewRouteSegments(routeData.newSegments || []);
    } catch (error) {
      console.error("Error fetching route data:", error);
      setSnackbar({
        open: true,
        message: "Error loading route data. Please refresh the page.",
        severity: "error",
      });
    }
  }, [id]);

   useEffect(() => {
    const ownerDetails = getOwnerDetailsFromLocalStorage();
    setIsSuperAdmin(ownerDetails?.role === 'superAdmin');
  }, []);

  useEffect(() => {
    getRouteData();
  }, [getRouteData]);

  /* Owners list */
  useEffect(() => {
    (async () => {
      try {
        const res = await OwnerServices.getAllOwners();
        setOwnerList(res || []);
      } catch (error) {
        console.error("Error while getting the owners", error);
        setSnackbar({
          open: true,
          message: "Error loading owners data.",
          severity: "error",
        });
      }
    })();
  }, []);

  /* Auto select owner via localStorage */
  useEffect(() => {
    if (ownerList.length > 0 && !assignedOwner) {
      const adminData = localStorage.getItem("admin");
      if (adminData) {
        try {
          const admin = JSON.parse(adminData);
          const adminEmail = admin.email;
          const matchingOwner = ownerList.find(
            (owner) => owner.email === adminEmail
          );

          if (matchingOwner) setAssignedOwner(matchingOwner._id);
        } catch (error) {
          console.error("Error parsing admin data from localStorage:", error);
        }
      }
    }
  }, [ownerList, assignedOwner]);

  useEffect(() => {
    if (!assignedOwner) return; // Do nothing if assignedOwner is empty

    const getAllVehicles = async () => {
      try {
        const vehicles = await VehicleServices.getVehiclesByOwnerId(
          assignedOwner
        );
        console.log("vehicles", vehicles);
        setVehicleList(Array.isArray(vehicles) ? vehicles : []);
      } catch (err) {
        console.error("Error while getting the vehicles", err);
        setVehicleList([]); // fallback
      }
    };

    getAllVehicles();
  }, [assignedOwner]);

  /* Coordinates label */
  useEffect(() => {
    if (mapCenter) {
      setCurrentCoordinates(
        `Lat: ${mapCenter.lat.toFixed(6)}, Lng: ${mapCenter.lng.toFixed(6)}`
      );
    }
  }, [mapCenter]);

  /* ───────── Stops Handlers ───────── */
  const handleAddStop = () => {
    setStops((prev) => [
      ...prev,
      {
        stopName: "",
        description: "",
        latitude: "",
        longitude: "",
        plannedArrivalTime: "",
        isEnabled: true,
        address: "",
        areaName: "",
        sequence: "",
      },
    ]);
  };

  const handleStopChange = (index, field, value) => {
    const newStops = stops.map((stop, i) =>
      index === i ? { ...stop, [field]: value } : stop
    );
    setStops(newStops);
  };

  const handleRemoveStop = (index) => {
    if (stops.length > 1) {
      const newStops = stops.filter((_, i) => i !== index);
      setStops(newStops);
    }
  };

  const handleAddStopInBetween = (index) => {
    const newStop = {
      stopName: "",
      description: "",
      latitude: "",
      longitude: "",
      plannedArrivalTime: "",
      isEnabled: true,
      address: "",
      areaName: "",
      sequence: "",
    };
    const newStops = [...stops];
    newStops.splice(index, 0, newStop);
    setStops(newStops);
  };

  /* ───────── Map Handlers ───────── */
  const onLoad = useCallback((m) => setMap(m), []);
  const onUnmount = useCallback(() => setMap(null), []);

  const handleMapClickForNewSegments = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setNewRouteSegments((prev) => [...prev, { latitude: lat, longitude: lng }]);
  };

  const handleUndoNewSegment = () => {
    setNewRouteSegments((prev) => (prev.length ? prev.slice(0, -1) : prev));
  };

  const handlePromoteNewSegments = async () => {
    setSegments(newRouteSegments);
    setNewRouteSegments([]);
    await saveRoute(newRouteSegments, []);
  };

  const handleMapClick = useCallback(
    (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      if (map) map.panTo({ lat, lng });
    },
    [map]
  );

  const handleMapCenterChanged = useCallback(() => {
    if (map && currentStopIndex !== null) {
      const center = map.getCenter();
      const lat = center.lat();
      const lng = center.lng();
      setMapCenter({ lat, lng });

      const newStops = [...stops];
      newStops[currentStopIndex] = {
        ...newStops[currentStopIndex],
        latitude: lat.toString(),
        longitude: lng.toString(),
      };
      setStops(newStops);

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          const newStopsWithAddress = [...newStops];
          newStopsWithAddress[currentStopIndex] = {
            ...newStopsWithAddress[currentStopIndex],
            address: results[0].formatted_address,
          };
          setStops(newStopsWithAddress);
        }
      });
    }
  }, [map, currentStopIndex, stops]);

  const openLocationSelector = (index) => {
    setCurrentStopIndex(index);
    const stop = stops[index];
    if (stop.latitude && stop.longitude) {
      setMapCenter({
        lat: parseFloat(stop.latitude),
        lng: parseFloat(stop.longitude),
      });
      setSearchValue(stop.address || `${stop.latitude}, ${stop.longitude}`);
    } else {
      setMapCenter(hospetCenter);
      setSearchValue("");
    }
    setShowMapDialog(true);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchValue.trim() || !map) return;
    setIsSearching(true);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { address: searchValue, componentRestrictions: { country: "IN" } },
      (results, status) => {
        setIsSearching(false);
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          map.panTo(location);
        } else {
          setSnackbar({
            open: true,
            message: "Location not found. Please try a different search term.",
            severity: "warning",
          });
        }
      }
    );
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setIsSearching(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setIsSearching(false);
          const newCenter = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          if (map) map.panTo(newCenter);
        },
        () => {
          setIsSearching(false);
          setSnackbar({
            open: true,
            message: "Unable to retrieve your location.",
            severity: "error",
          });
        }
      );
    }
  };

  const onLoadPolyline = useCallback((polyline) => {
    polylineRef.current = polyline;
    const path = polyline.getPath();

    const updateSegments = () => {
      const pathArray = path.getArray();
      const currentSegments = segmentsRef.current;
      const newSegments = pathArray.map((point, index) => ({
        ...currentSegments[index],
        latitude: point.lat(),
        longitude: point.lng(),
      }));
      setSegments(newSegments);
    };

    const listeners = [];
    listeners.push(
      window.google.maps.event.addListener(path, "set_at", updateSegments)
    );
    listeners.push(
      window.google.maps.event.addListener(path, "insert_at", updateSegments)
    );
    listeners.push(
      window.google.maps.event.addListener(path, "remove_at", updateSegments)
    );
    polylineRef.current.pathListeners = listeners;

    return () => {
      if (polylineRef.current?.pathListeners) {
        polylineRef.current.pathListeners.forEach((l) =>
          window.google.maps.event.removeListener(l)
        );
      }
    };
  }, []);

  const handleMarkerClick = (segment, index) => {
    setSelectedSegmentIndex(index);
    setMapCenter({
      lat: parseFloat(segment.latitude),
      lng: parseFloat(segment.longitude),
    });
  };

  const confirmSegmentDelete = (index) => {
    setSegmentToDelete(index);
    setShowDeleteConfirm(true);
    setSelectedSegmentIndex(null);
  };

  const handleSegmentDelete = async (index) => {
    if (segments.length <= 1) {
      setSnackbar({
        open: true,
        message:
          "Cannot delete the last segment. Route must have at least one segment.",
        severity: "warning",
      });
      return;
    }

    const newSegments = segments.filter((_, i) => i !== index);
    setSegments(newSegments);
    setSelectedSegmentIndex(null);

    try {
      const updatedRoute = {
        route_name: routeName,
        stops: stops.map((stop) => ({
          stopName: stop.stopName,
          description: stop.description,
          isEnabled: stop.isEnabled !== undefined ? stop.isEnabled : true,
          latitude: parseFloat(stop.latitude) || 0,
          longitude: parseFloat(stop.longitude) || 0,
          plannedArrivalTime: stop.plannedArrivalTime,
          actuallArrivalTime: stop.actuallArrivalTime || "",
          address: stop.address || "",
          areaName: stop.areaName || "",
          sequence: parseInt(stop.sequence) || 0,
        })),
        segments: newSegments,
        assigned_owner_id: assignedOwner,
        approvalNeeded,
        routeType,
        returnRouteId: returnRouteId || null,
        approval_status: approvalStatus,
        isTrainingMode,
        newSegments: newRouteSegments,
        alias_number: aliasNumber,
        assigned_vehicle_id: assignedVehicle || null,
      };

      await VehicleRouteServices.updateVehicleRouteById(id, updatedRoute);
      await getRouteData();
      setSnackbar({
        open: true,
        message: "Segment deleted successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error updating route after segment deletion:", error);
      setSegments([...segments]); // revert
      setSnackbar({
        open: true,
        message: "Failed to delete segment. Please try again.",
        severity: "error",
      });
    }
  };

  /* ───────── Save / Submit ───────── */
  const saveRoute = async (segmentsToSave, newSegmentsToSave) => {
    setIsSubmitting(true);
    try {
      const processedStops = stops.map((stop) => ({
        stopName: stop.stopName,
        description: stop.description,
        isEnabled: stop.isEnabled !== undefined ? stop.isEnabled : true,
        latitude: parseFloat(stop.latitude) || 0,
        longitude: parseFloat(stop.longitude) || 0,
        plannedArrivalTime: stop.plannedArrivalTime,
        actuallArrivalTime: stop.actuallArrivalTime || "",
        address: stop.address || "",
        areaName: stop.areaName || "",
        sequence: parseInt(stop.sequence) || 0,
        isPrimaryStop: stop.isPrimaryStop,
      }));

      const processedSegments = segmentsToSave.map((segment) => ({
        ...segment,
        latitude: parseFloat(segment.latitude),
        longitude: parseFloat(segment.longitude),
      }));

      const updatedRoute = {
        route_name: routeName,
        startTime: startTime,
        endTime: endTime,
        stops: processedStops,
        segments: processedSegments,
        assigned_owner_id: assignedOwner,
        approvalNeeded,
        routeType,
        returnRouteId: returnRouteId || null,
        approval_status: approvalStatus,
        isTrainingMode,
        newSegments: newSegmentsToSave,
        alias_number: aliasNumber,
        assigned_vehicle_id: assignedVehicle || null,
      };

      await VehicleRouteServices.updateVehicleRouteById(id, updatedRoute);
      setSnackbar({
        open: true,
        message: "Route updated successfully!",
        severity: "success",
      });
      await getRouteData();
    } catch (error) {
      console.error("Error updating vehicle route:", error);
      setSnackbar({
        open: true,
        message: "Failed to update route. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await saveRoute(segments, newRouteSegments);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  /* ───────── UI helpers ───────── */
  const formatDate = (date) => (date ? date.toLocaleString() : "N/A");
  const getApprovalStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "success";
      case "Rejected":
        return "error";
      case "Pending":
        return "warning";
      default:
        return "default";
    }
  };

  /* Polyline path trimming based on second stop proximity */
  const polylinePath = useMemo(() => {
    if (segments.length === 0) return [];
    if (!window.google || !window.google.maps || !window.google.maps.geometry) {
      return segments.map((s) => ({
        lat: parseFloat(s.latitude),
        lng: parseFloat(s.longitude),
      }));
    }

    if (stops.length < 2) {
      return segments.map((s) => ({
        lat: parseFloat(s.latitude),
        lng: parseFloat(s.longitude),
      }));
    }

    const secondStop = stops[1];
    if (!secondStop.latitude || !secondStop.longitude) {
      return segments.map((s) => ({
        lat: parseFloat(s.latitude),
        lng: parseFloat(s.longitude),
      }));
    }

    const secondStopCoords = new window.google.maps.LatLng(
      parseFloat(secondStop.latitude),
      parseFloat(secondStop.longitude)
    );

    let closestSegmentIndex = -1;
    let minDistance = Infinity;

    segments.forEach((segment, index) => {
      const segmentCoords = new window.google.maps.LatLng(
        parseFloat(segment.latitude),
        parseFloat(segment.longitude)
      );
      const distance =
        window.google.maps.geometry.spherical.computeDistanceBetween(
          secondStopCoords,
          segmentCoords
        );
      if (distance < minDistance) {
        minDistance = distance;
        closestSegmentIndex = index;
      }
    });

    const trimmed =
      closestSegmentIndex !== -1
        ? segments.slice(closestSegmentIndex)
        : segments;

    return trimmed.map((s) => ({
      lat: parseFloat(s.latitude),
      lng: parseFloat(s.longitude),
    }));
  }, [stops, segments]);

  /* ───────── Render guards ───────── */
  if (loadError) return <Alert severity="error">Error loading maps</Alert>;
  if (!isLoaded) return <CircularProgress />;

  /* ───────── UI ───────── */
  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <Card elevation={3} sx={{ borderRadius: 2 }}>
        <CardContent>
          {/* Summary header */}
          <Box sx={{ mb: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              flexWrap="wrap"
              rowGap={1}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h5" fontWeight="bold">
                  Edit Vehicle Route
                </Typography>
                <Chip
                  icon={<InfoIcon />}
                  label={routeType || "N/A"}
                  color={
                    routeType === "pickup"
                      ? "success"
                      : routeType === "drop"
                      ? "info"
                      : "default"
                  }
                  size="small"
                />
                <Chip
                  label={approvalStatus || "Pending"}
                  color={getApprovalStatusColor(approvalStatus)}
                  size="small"
                />
                {isTrainingMode && (
                  <Chip label="Training Mode" color="info" size="small" />
                )}
              </Stack>
              {createdAt && (
                <Typography variant="caption" color="text.secondary">
                  <CalendarIcon sx={{ fontSize: 14, mr: 0.5 }} />
                  Created: {formatDate(createdAt)}
                </Typography>
              )}
            </Stack>
          </Box>

          {/* Tabs */}
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant={isSmall ? "scrollable" : "standard"}
            sx={{ mb: 2 }}
          >
            <Tab
              icon={<InfoIcon fontSize="small" />}
              iconPosition="start"
              label="Details"
            />
            <Tab
              icon={<EditOutlinedIcon fontSize="small" />}
              iconPosition="start"
              label={`Stops (${stops.length})`}
            />
            <Tab
              icon={<MapOutlinedIcon fontSize="small" />}
              iconPosition="start"
              label={`Map (${segments.length})`}
            />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {/* Details tab */}
            {/* {tab === 0 && (
              <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Route Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Route Name"
                      value={routeName}
                      onChange={(e) => setRouteName(e.target.value)}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Start Time"
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      required
                      disabled={isSubmitting}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="End Time"
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      required
                      disabled={isSubmitting}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Assigned Owner</InputLabel>
                      <Select
                        value={assignedOwner}
                        label="Assigned Owner"
                        onChange={(e) => setAssignedOwner(e.target.value)}
                        disabled={ownerList.length === 0 || isSubmitting}
                      >
                        {ownerList.map((owner) => (
                          <MenuItem key={owner._id} value={owner._id}>
                            {owner.name || owner.email}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Route Type</InputLabel>
                      <Select
                        value={routeType}
                        label="Route Type"
                        onChange={(e) => setRouteType(e.target.value)}
                        disabled={isSubmitting}
                      >
                        <MenuItem value="pickup">Pickup</MenuItem>
                        <MenuItem value="drop">Drop</MenuItem>
                        <MenuItem value="both">Both</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Alias Number"
                      value={aliasNumber}
                      onChange={(e) => setAliasNumber(e.target.value)}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Approval Status</InputLabel>
                      <Select
                        value={approvalStatus}
                        label="Approval Status"
                        onChange={(e) => setApprovalStatus(e.target.value)}
                        disabled={isSubmitting}
                      >
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Approved">Approved</MenuItem>
                        <MenuItem value="Rejected">Rejected</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Assigned Bus</InputLabel>
                      <Select
                        value={
                          vehicleList.some((v) => v._id === assignedVehicle)
                            ? assignedVehicle
                            : ""
                        }
                        label="Assigned Vehicle"
                        onChange={(e) => setAssignedVehicle(e.target.value)}
                      >
                        {vehicleList.map((vehicle) => (
                          <MenuItem key={vehicle._id} value={vehicle._id}>
                            {vehicle.alias_number}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={approvalNeeded}
                          onChange={(e) => setApprovalNeeded(e.target.checked)}
                          disabled={isSubmitting}
                        />
                      }
                      label="Approval Required"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isTrainingMode}
                          onChange={(e) => setIsTrainingMode(e.target.checked)}
                          disabled={isSubmitting}
                        />
                      }
                      label="Training Mode"
                    />
                  </Grid>
                </Grid>
              </Paper>
            )} */}

            {tab === 0 && (
          <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Route Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Route Name"
                  value={routeName}
                  onChange={(e) => setRouteName(e.target.value)}
                  required
                  disabled={isSubmitting || !isSuperAdmin}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                  disabled={isSubmitting || !isSuperAdmin}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                  disabled={isSubmitting || !isSuperAdmin}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Assigned Owner</InputLabel>
                  <Select
                    value={assignedOwner}
                    label="Assigned Owner"
                    onChange={(e) => setAssignedOwner(e.target.value)}
                    disabled={!isSuperAdmin || ownerList.length === 0 || isSubmitting}
                  >
                    {ownerList.map((owner) => (
                      <MenuItem key={owner._id} value={owner._id}>
                        {owner.name || owner.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Route Type</InputLabel>
                  <Select
                    value={routeType}
                    label="Route Type"
                    onChange={(e) => setRouteType(e.target.value)}
                    disabled={!isSuperAdmin || isSubmitting}
                  >
                    <MenuItem value="pickup">Pickup</MenuItem>
                    <MenuItem value="drop">Drop</MenuItem>
                    <MenuItem value="both">Both</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Alias Number"
                  value={aliasNumber}
                  onChange={(e) => setAliasNumber(e.target.value)}
                  required
                  disabled={isSubmitting || !isSuperAdmin}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Approval Status</InputLabel>
                  <Select
                    value={approvalStatus}
                    label="Approval Status"
                    onChange={(e) => setApprovalStatus(e.target.value)}
                    disabled={isSubmitting || !isSuperAdmin}
                  >
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Approved">Approved</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Assigned Bus</InputLabel>
                  <Select
                    value={vehicleList.some((v) => v._id === assignedVehicle) ? assignedVehicle : ""}
                    label="Assigned Vehicle"
                    onChange={(e) => setAssignedVehicle(e.target.value)}
                    disabled={!isSuperAdmin}
                  >
                    {vehicleList.map((vehicle) => (
                      <MenuItem key={vehicle._id} value={vehicle._id}>
                        {vehicle.alias_number}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={approvalNeeded}
                      onChange={(e) => setApprovalNeeded(e.target.checked)}
                      disabled={isSubmitting || !isSuperAdmin}
                    />
                  }
                  label="Approval Required"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isTrainingMode}
                      onChange={(e) => setIsTrainingMode(e.target.checked)}
                      disabled={isSubmitting || !isSuperAdmin}
                    />
                  }
                  label="Training Mode"
                />
              </Grid>
            </Grid>
          </Paper>
        )}

            {/* Stops tab */}
            {tab === 1 && (
              <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="h6" color="primary">
                    Route Stops ({stops.length})
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddStop}
                      disabled={isSubmitting || !isSuperAdmin}
                  >
                    Add Stop
                  </Button>
                </Stack>

                <Stack spacing={1}>
                  {stops.map((stop, index) => (
                    <Accordion key={index} disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ width: "100%", pr: 1 }}
                        >
                          <Chip size="small" label={`#${index + 1}`} />
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{ flex: 1 }}
                          >
                            {stop.stopName || `Stop ${index + 1}`}
                          </Typography>
                          {stop.latitude && stop.longitude && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {parseFloat(stop.latitude).toFixed(5)},{" "}
                              {parseFloat(stop.longitude).toFixed(5)}
                            </Typography>
                          )}
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={1.5} alignItems="center">
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              label={`Stop ${index + 1} Name`}
                              value={stop.stopName}
                              onChange={(e) =>
                                handleStopChange(
                                  index,
                                  "stopName",
                                  e.target.value
                                )
                              }
                              required
                                disabled={isSubmitting || !isSuperAdmin}
                            />
                          </Grid>

                          <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Description"
                              value={stop.description}
                              onChange={(e) =>
                                handleStopChange(
                                  index,
                                  "description",
                                  e.target.value
                                )
                              }
                                disabled={isSubmitting || !isSuperAdmin}
                            />
                          </Grid>

                          <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Area Name"
                              value={stop.areaName}
                              onChange={(e) =>
                                handleStopChange(
                                  index,
                                  "areaName",
                                  e.target.value
                                )
                              }
                               disabled={isSubmitting || !isSuperAdmin}
                            />
                          </Grid>

                          <Grid item xs={12} md={1.2}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Sequence"
                              type="number"
                              value={stop.sequence}
                              onChange={(e) =>
                                handleStopChange(
                                  index,
                                  "sequence",
                                  e.target.value
                                )
                              }
                                disabled={isSubmitting || !isSuperAdmin}
                            />
                          </Grid>

                          <Grid item xs={12} md={2.3}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Planned Arrival"
                              type="text"
                              placeholder="HH:MM AM/PM"
                              value={stop.plannedArrivalTime}
                              onChange={(e) =>
                                handleStopChange(
                                  index,
                                  "plannedArrivalTime",
                                  e.target.value
                                )
                              }
                              required
                               disabled={isSubmitting || !isSuperAdmin}
                            />
                          </Grid>

                          <Grid item xs={12} md={1.5}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<MapIcon />}
                              onClick={() => openLocationSelector(index)}
                               disabled={isSubmitting || !isSuperAdmin}
                              fullWidth
                            >
                              {stop.latitude ? "Change" : "Select"}
                            </Button>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                            >
                              <TextField
                                size="small"
                                label="Latitude"
                                value={stop.latitude}
                                onChange={(e) =>
                                  handleStopChange(
                                    index,
                                    "latitude",
                                    e.target.value
                                  )
                                }
                                  disabled={isSubmitting || !isSuperAdmin}
                              />
                              <TextField
                                size="small"
                                label="Longitude"
                                value={stop.longitude}
                                onChange={(e) =>
                                  handleStopChange(
                                    index,
                                    "longitude",
                                    e.target.value
                                  )
                                }
                               disabled={isSubmitting || !isSuperAdmin}
                              />
                            </Stack>
                            {stop.address && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5, display: "block" }}
                              >
                                📍 {stop.address}
                              </Typography>
                            )}
                            {stop.latitude && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5, display: "block" }}
                              >
                                <LocationOn
                                  sx={{
                                    fontSize: "0.9rem",
                                    verticalAlign: "middle",
                                  }}
                                />
                                {`Lat: ${parseFloat(stop.latitude).toFixed(
                                  5
                                )}, Lng: ${parseFloat(stop.longitude).toFixed(
                                  5
                                )}`}
                              </Typography>
                            )}
                          </Grid>

                          <Grid item xs={12} md="auto">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!stop.isEnabled}
                                  onChange={(e) =>
                                    handleStopChange(
                                      index,
                                      "isEnabled",
                                      e.target.checked
                                    )
                                  }
                                    disabled={isSubmitting || !isSuperAdmin}
                                />
                              }
                              label="Enabled"
                            />
                          </Grid>

                          <Grid item xs={12} md="auto">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!stop.isPrimaryStop}
                                  onChange={(e) =>
                                    handleStopChange(
                                      index,
                                      "isPrimaryStop",
                                      e.target.checked
                                    )
                                  }
                                disabled={isSubmitting || !isSuperAdmin}
                                />
                              }
                              label="isPrimaryStop"
                            />

                            <IconButton
                              color="error"
                              onClick={() => handleRemoveStop(index)}
                              disabled={stops.length === 1 || isSubmitting || !isSuperAdmin}
                            >
                              <DeleteIcon />
                            </IconButton>
                            <Button
                              size="small"
                              sx={{ ml: 1 }}
                              onClick={() => handleAddStopInBetween(index + 1)}
                              disabled={isSubmitting || !isSuperAdmin}
                            >
                              Add Here
                            </Button>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Map tab */}
            {tab === 2 && (
              <Paper elevation={1} sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="h6" color="primary">
                    Edit Route Segments ({segments.length})
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    value={editMode}
                    exclusive
                    onChange={(_, v) => v && setEditMode(v)}
                  >
                    <ToggleButton value="segments">
                      <RouteIcon fontSize="small" />
                      &nbsp;Segments
                    </ToggleButton>
                    <ToggleButton value="stops">
                      <MapOutlinedIcon fontSize="small" />
                      &nbsp;Stops
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>

                <Box sx={{ position: "relative" }}>
                  {/* Floating Toolbar */}
                  {/* <Box
                    sx={{
                      position: "absolute",
                      zIndex: 2,
                      top: 8,
                      left: 8,
                      right: 8,
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      p: 1,
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      boxShadow: 2,
                    }}
                  >
                    <form onSubmit={handleSearchSubmit} style={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search for a location"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {isSearching ? (
                                <CircularProgress size={16} />
                              ) : (
                                <SearchIcon fontSize="small" />
                              )}
                            </InputAdornment>
                          ),
                          endAdornment: searchValue && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => setSearchValue("")}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </form>
                    <Tooltip title="Use Current Location">
                      <IconButton size="small" onClick={getCurrentLocation}>
                        <MyLocationIcon />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {currentCoordinates}
                    </Typography>
                  </Box> */}

                  {/* Map */}
                  <Box sx={mapContainerStyle}>
                    <GoogleMap
                      mapContainerStyle={{ width: "100%", height: "100%" }}
                      center={mapCenter}
                      zoom={15}
                      onLoad={onLoad}
                      onUnmount={onUnmount}
                      onClick={
                        editMode === "segments"
                          ? handleMapClickForNewSegments
                          : undefined
                      }
                      options={{
                        streetViewControl: false,
                        mapTypeControl: false,
                      }}
                    >
                      {/* Main route polyline (draggable) */}
                      <Polyline
                        key={`polyline-${polylinePath.length}`}
                        path={polylinePath}
                        options={{
                          strokeColor: "#00AA00",
                          strokeOpacity: 0.8,
                          strokeWeight: 3,
                          editable: true,
                          draggable: false,
                          geodesic: true,
                          icons: [
                            {
                              icon: {
                                path: window.google.maps.SymbolPath
                                  .FORWARD_CLOSED_ARROW,
                                scale: 2,
                                strokeColor: "#FF0000",
                              },
                              repeat: "120px",
                            },
                          ],
                        }}
                        onLoad={onLoadPolyline}
                      />

                      {/* Existing stops markers */}
                      {stops.map(
                        (stop, index) =>
                          stop.latitude &&
                          stop.longitude && (
                            <Marker
                              key={`stop-marker-${index}`}
                              position={{
                                lat: parseFloat(stop.latitude),
                                lng: parseFloat(stop.longitude),
                              }}
                              label={stop.stopName}
                              title={stop.stopName || `Stop ${index + 1}`}
                              options={{
                                icon: {
                                  url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
                                  scaledSize: new window.google.maps.Size(
                                    32,
                                    32
                                  ),
                                },
                              }}
                            />
                          )
                      )}

                      {/* New route segments preview polyline (editable) */}
                      <Polyline
                        path={newRouteSegments.map((s) => ({
                          lat: s.latitude,
                          lng: s.longitude,
                        }))}
                        options={{
                          strokeColor: "#0000FF",
                          strokeOpacity: 0.8,
                          strokeWeight: 2,
                          editable: true,
                        }}
                      />

                      {/* Preview markers */}
                      {newRouteSegments.map((segment, index) => (
                        <Marker
                          key={`new-segment-${index}`}
                          position={{
                            lat: segment.latitude,
                            lng: segment.longitude,
                          }}
                          onClick={() => handleMarkerClick(segment, index)}
                        />
                      ))}
                    </GoogleMap>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Sticky action bar */}
            <Box
              sx={{
                position: "sticky",
                bottom: -16,
                mt: 2,
                py: 1,
                bgcolor: "background.paper",
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                zIndex: 1,
              }}
            >
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<UndoIcon />}
                  onClick={handleUndoNewSegment}
                  disabled={newRouteSegments.length === 0 || isSubmitting || !isSuperAdmin}
                >
                  Undo Last Point
                </Button>
                <Button
                  variant="contained"
                  onClick={handlePromoteNewSegments}
                  disabled={newRouteSegments.length === 0 || isSubmitting|| !isSuperAdmin}
                >
                  Set as Actual Segments
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={isSubmitting|| !isSuperAdmin}
                >
                  {isSubmitting ? "Updating…" : "Update Route"}
                </Button>
              </Stack>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* Map Dialog for Stop Location */}
      <Dialog
        open={showMapDialog}
        onClose={() => !isSubmitting && setShowMapDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Select Location for{" "}
          {stops[currentStopIndex]?.stopName || `Stop ${currentStopIndex + 1}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <form onSubmit={handleSearchSubmit}>
              <TextField
                fullWidth
                inputRef={searchInputRef}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search for a location"
                variant="outlined"
                size="small"
                disabled={isSubmitting || !isSuperAdmin}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {isSearching ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SearchIcon />
                      )}
                    </InputAdornment>
                  ),
                  endAdornment: searchValue && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setSearchValue("")}
                        size="small"
                        disabled={isSubmitting || !isSuperAdmin}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </form>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Button
                startIcon={<MyLocationIcon />}
                onClick={getCurrentLocation}
                size="small"
                disabled={isSubmitting||!isSuperAdmin}
              >
                Use Current Location
              </Button>
              <Typography variant="caption" color="text.secondary">
                {currentCoordinates}
              </Typography>
            </Box>
          </Box>

          <Box sx={mapContainerStyle}>
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={mapCenter}
              zoom={15}
              onLoad={onLoad}
              onUnmount={onUnmount}
              onClick={!isSubmitting ? handleMapClick : undefined}
              onIdle={!isSubmitting ? handleMapCenterChanged : undefined}
              options={{ streetViewControl: false, mapTypeControl: false }}
            >
              {/* Central Pin */}
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -100%)",
                  pointerEvents: "none",
                }}
              >
                <img
                  src={googlePin}
                  alt="Map Pin"
                  style={{ width: "40px", height: "40px" }}
                />
              </Box>

              {/* Markers for existing stops */}
              {stops.map(
                (stop, index) =>
                  stop.latitude &&
                  stop.longitude && (
                    <Marker
                      key={`stop-marker-dialog-${index}`}
                      position={{
                        lat: parseFloat(stop.latitude),
                        lng: parseFloat(stop.longitude),
                      }}
                      label={stop.stopName}
                      title={stop.stopName || `Stop ${index + 1}`}
                    />
                  )
              )}
            </GoogleMap>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowMapDialog(false)}
            variant="outlined"
            disabled={isSubmitting || !isSuperAdmin}
          >
            Cancel
          </Button>
          <Button
            onClick={() => setShowMapDialog(false)}
            variant="contained"
           disabled={isSubmitting || !isSuperAdmin}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm segment deletion */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => !isSubmitting && setShowDeleteConfirm(false)}
        maxWidth="sm"
      >
        <DialogTitle>Confirm Segment Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete Segment{" "}
            {segmentToDelete !== null ? segmentToDelete + 1 : ""}? This action
            cannot be undone and will immediately update the route.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowDeleteConfirm(false);
              setSegmentToDelete(null);
            }}
            disabled={isSubmitting || !isSuperAdmin}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (segmentToDelete !== null)
                handleSegmentDelete(segmentToDelete);
              setShowDeleteConfirm(false);
              setSegmentToDelete(null);
            }}
            color="error"
            variant="contained"
  disabled={isSubmitting || !isSuperAdmin}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditVehicleRoute;
